import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Lock, ArrowLeft, ArrowRight, CheckCircle2, Phone } from "lucide-react";
import { PRISON_BY_SLUG, PRISONS } from "./PrisonPages";

const usePageMeta = (title, description, canonical) => {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;
    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc ? desc.getAttribute("content") : null;
    if (desc && description) desc.setAttribute("content", description);

    let canon = document.querySelector('link[rel="canonical"]');
    const prevCanon = canon ? canon.getAttribute("href") : null;
    if (canonical) {
      if (!canon) {
        canon = document.createElement("link");
        canon.setAttribute("rel", "canonical");
        document.head.appendChild(canon);
      }
      canon.setAttribute("href", canonical);
    }

    return () => {
      document.title = prevTitle;
      if (desc && prevDesc != null) desc.setAttribute("content", prevDesc);
      if (canon && prevCanon != null) canon.setAttribute("href", prevCanon);
    };
  }, [title, description, canonical]);
};

// ============================================================================
// STATE DATA — each state gets unique correctional centres + slightly varied
// copy to avoid Google flagging duplicate content. All factual references are
// publicly available correctional facility names.
// ============================================================================
const STATES = {
  qld: {
    slug: "qld",
    name: "Queensland",
    abbr: "QLD",
    title: "Cheap Prison Calls Queensland | Local Number at just $1 a day",
    description:
      "Get a local Queensland landline number for prison calls at just $1 a day. Unlimited calls forwarded to your mobile from any QLD correctional centre. Cancel anytime.",
    h1: "Cheap Prison Calls Queensland",
    intro:
      "Phone calls from Queensland correctional centres to mobile phones add up fast. Cellblock B gives families across Brisbane, Gold Coast, Sunshine Coast, Townsville, Cairns and regional QLD a local Queensland landline number at just $1 a day. The inmate dials it as a local call — and we bridge it straight to your mobile. Unlimited calls. No lock-in.",
    centres: [
      "Arthur Gorrie Correctional Centre",
      "Borallon Training and Correctional Centre",
      "Brisbane Correctional Centre",
      "Brisbane Women's Correctional Centre",
      "Capricornia Correctional Centre",
      "Lotus Glen Correctional Centre",
      "Maryborough Correctional Centre",
      "Numinbah Correctional Centre",
      "Palen Creek Correctional Centre",
      "Southern Queensland Correctional Centre",
      "Townsville Correctional Centre",
      "Wolston Correctional Centre",
      "Woodford Correctional Centre",
    ],
    keyword: "Queensland",
  },
  nsw: {
    slug: "nsw",
    name: "New South Wales",
    abbr: "NSW",
    title: "Cheap Prison Calls NSW | Local Number at just $1 a day",
    description:
      "Cellblock B provides a local NSW landline number for prison calls at just $1 a day. Unlimited calls forwarded to your mobile from any New South Wales correctional centre.",
    h1: "Cheap Prison Calls NSW",
    intro:
      "Cellblock B helps NSW families across Sydney, Newcastle, Wollongong, Central Coast and regional New South Wales stay connected without the bill shock. For $1 a day ($30 every 30 days) (or $60 every 60 days), we set you up with a local NSW landline number that the inmate calls as a standard local call — and we forward it straight to your mobile.",
    centres: [
      "Bathurst Correctional Centre",
      "Cessnock Correctional Centre",
      "Cooma Correctional Centre",
      "Dillwynia Correctional Centre",
      "Goulburn Correctional Centre",
      "Junee Correctional Centre",
      "Kirkconnell Correctional Centre",
      "Lithgow Correctional Centre",
      "Long Bay Correctional Complex",
      "Macquarie Correctional Centre",
      "Mid North Coast Correctional Centre",
      "Parklea Correctional Centre",
      "Silverwater Correctional Complex",
      "South Coast Correctional Centre",
      "Wellington Correctional Centre",
    ],
    keyword: "NSW",
  },
  vic: {
    slug: "vic",
    name: "Victoria",
    abbr: "VIC",
    title: "Cheap Prison Calls Victoria | Local Number at just $1 a day",
    description:
      "Reduce Victoria prison call costs with a local VIC landline number from Cellblock B. $1 a day. Unlimited calls, cancel anytime.",
    h1: "Cheap Prison Calls Victoria",
    intro:
      "If a loved one is at a Victorian correctional centre, Cellblock B gives families across Melbourne, Geelong, Ballarat, Bendigo and regional Victoria a local VIC landline number that drastically reduces what you pay to stay in touch. Unlimited calls forwarded to your mobile, at just $1 a day.",
    centres: [
      "Barwon Prison",
      "Beechworth Correctional Centre",
      "Dame Phyllis Frost Centre",
      "Dhurringile Prison",
      "Fulham Correctional Centre",
      "Hopkins Correctional Centre",
      "Langi Kal Kal Prison",
      "Loddon Prison",
      "Marngoneet Correctional Centre",
      "Melbourne Assessment Prison",
      "Metropolitan Remand Centre",
      "Middleton Prison",
      "Port Phillip Prison",
      "Ravenhall Correctional Centre",
      "Tarrengower Prison",
    ],
    keyword: "Victoria",
  },
  wa: {
    slug: "wa",
    name: "Western Australia",
    abbr: "WA",
    title: "Cheap Prison Calls Western Australia | Local WA Number",
    description:
      "Local WA landline number for prison calls at just $1 a day. Cellblock B forwards unlimited calls to your mobile from any Western Australia correctional facility.",
    h1: "Cheap Prison Calls Western Australia",
    intro:
      "Cellblock B gives families across Perth, Fremantle, Mandurah, Bunbury and regional Western Australia a local WA landline number that inmates can dial as a normal local call — saving you significant money compared to expensive per-minute mobile call rates.",
    centres: [
      "Acacia Prison",
      "Albany Regional Prison",
      "Bandyup Women's Prison",
      "Boronia Pre-release Centre for Women",
      "Broome Regional Prison",
      "Bunbury Regional Prison",
      "Casuarina Prison",
      "Eastern Goldfields Regional Prison",
      "Greenough Regional Prison",
      "Hakea Prison",
      "Karnet Prison Farm",
      "Pardelup Prison Farm",
      "Roebourne Regional Prison",
      "Wandoo Reintegration Facility",
      "West Kimberley Regional Prison",
      "Wooroloo Prison Farm",
    ],
    keyword: "Western Australia",
  },
  sa: {
    slug: "sa",
    name: "South Australia",
    abbr: "SA",
    title: "Cheap Prison Calls South Australia | SA Local Number",
    description:
      "Get a local South Australia landline for prison calls at just $1 a day with Cellblock B. Unlimited calls forwarded to your mobile from any SA correctional facility.",
    h1: "Cheap Prison Calls South Australia",
    intro:
      "Cellblock B sets families across Adelaide, Mount Gambier, Port Augusta and regional South Australia up with a local SA landline number that inmates can dial as a local call. We bridge the call to your mobile so you only ever pay one flat subscription — $1 a day ($30 every 30 days), with unlimited calls and no lock-in.",
    centres: [
      "Adelaide Pre-release Centre",
      "Adelaide Remand Centre",
      "Adelaide Women's Prison",
      "Cadell Training Centre",
      "Mobilong Prison",
      "Mount Gambier Prison",
      "Port Augusta Prison",
      "Port Lincoln Prison",
      "Yatala Labour Prison",
    ],
    keyword: "South Australia",
  },
  tas: {
    slug: "tas",
    name: "Tasmania",
    abbr: "TAS",
    title: "Cheap Prison Calls Tasmania | TAS Local Number",
    description:
      "Cellblock B provides a Tasmania local landline for prison calls at just $1 a day. Unlimited calls forwarded to your mobile from Risdon, Mary Hutchinson and Hayes prisons.",
    h1: "Cheap Prison Calls Tasmania",
    intro:
      "For Tasmanian families, every prison call to a mobile gets expensive quickly. Cellblock B gives you a local Tasmania landline number that inmates dial as a normal local call — bridged straight to your mobile. $1 a day ($30 every 30 days), unlimited calls, cancel anytime.",
    centres: [
      "Hayes Prison Farm",
      "Launceston Reception Prison",
      "Mary Hutchinson Women's Prison",
      "Risdon Prison Complex",
      "Ron Barwick Minimum Security Prison",
    ],
    keyword: "Tasmania",
  },
  act: {
    slug: "act",
    name: "Australian Capital Territory",
    abbr: "ACT",
    title: "Cheap Prison Calls ACT | Alexander Maconochie Centre Calls",
    description:
      "Local ACT landline for Alexander Maconochie Centre prison calls at just $1 a day. Cellblock B forwards unlimited calls to your mobile. Cancel anytime.",
    h1: "Cheap Prison Calls ACT",
    intro:
      "The Alexander Maconochie Centre is the ACT's only correctional facility. Cellblock B gives Canberra families a local ACT landline number that inmates can dial as a local call, so you don't get hit with mobile call rates every time you pick up. $1 a day ($30 every 30 days) with unlimited calls.",
    centres: ["Alexander Maconochie Centre"],
    keyword: "ACT",
  },
  nt: {
    slug: "nt",
    name: "Northern Territory",
    abbr: "NT",
    title: "Cheap Prison Calls NT | Northern Territory Local Number",
    description:
      "Local Northern Territory landline for prison calls at just $1 a day. Cellblock B bridges unlimited calls to your mobile from Darwin and Alice Springs Correctional Centres.",
    h1: "Cheap Prison Calls Northern Territory",
    intro:
      "Cellblock B gives Top End families a local Northern Territory landline number that inmates at Darwin and Alice Springs Correctional Centres can dial as a normal local call. We forward it to your mobile so you only pay one flat $1 a day ($30 every 30 days) — no per-minute mobile rates.",
    centres: [
      "Alice Springs Correctional Centre",
      "Barkly Work Camp",
      "Datjala Work Camp",
      "Darwin Correctional Centre",
    ],
    keyword: "NT",
  },
};

const Hdr = () => (
  <header
    className="border-b"
    style={{
      borderColor: "var(--line)",
      background: "rgba(8,8,10,0.85)",
      backdropFilter: "blur(8px)",
    }}
  >
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-2.5" data-testid="state-back-home">
        <div
          className="w-9 h-9 rounded-md flex items-center justify-center"
          style={{ background: "var(--accent)", color: "#0b0b0c" }}
        >
          <Lock size={18} strokeWidth={2.5} />
        </div>
        <div className="font-display text-xl">CELLBLOCK B</div>
      </Link>
      <div className="flex items-center gap-5 text-sm">
        <Link to="/" style={{ color: "var(--muted)" }} className="hover:text-white transition hidden sm:inline">
          Home
        </Link>
        <Link to="/#pricing" style={{ color: "var(--muted)" }} className="hover:text-white transition hidden sm:inline">
          Pricing
        </Link>
        <Link
          to="/#signup"
          className="btn-primary"
          data-testid="state-get-number-cta"
          onClick={() =>
            window.cbbTrack &&
            window.cbbTrack("begin_signup", { source: "state_header" })
          }
        >
          Get My Number <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  </header>
);

const Ftr = () => (
  <footer className="border-t mt-20" style={{ borderColor: "var(--line)", background: "var(--bg-2)" }}>
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 grid sm:grid-cols-3 gap-8 text-sm" style={{ color: "var(--muted)" }}>
      <div>
        <div className="font-display text-xl text-white">CELLBLOCK B</div>
        <p className="mt-2">Australia's cheapest prison call numbers. $1 a day.</p>
      </div>
      <div>
        <div className="text-xs uppercase tracking-[0.2em] mb-3">By state</div>
        <ul className="space-y-1.5">
          {Object.values(STATES).map((s) => (
            <li key={s.slug}>
              <Link to={`/${s.slug}-prison-calls`} className="hover:text-white transition">
                {s.name}
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <div className="text-xs uppercase tracking-[0.2em] mb-3">Site</div>
        <ul className="space-y-1.5">
          <li><Link to="/" className="hover:text-white transition">Home</Link></li>
          <li><Link to="/#pricing" className="hover:text-white transition">Pricing</Link></li>
          <li><Link to="/#contact" className="hover:text-white transition">Contact</Link></li>
          <li><Link to="/privacy-policy" className="hover:text-white transition">Privacy Policy</Link></li>
          <li><Link to="/refund-policy" className="hover:text-white transition">Refund Policy</Link></li>
          <li><Link to="/terms-and-conditions" className="hover:text-white transition">Terms &amp; Conditions</Link></li>
        </ul>
      </div>
    </div>
    <div className="border-t" style={{ borderColor: "var(--line)" }}>
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-5 text-xs flex flex-wrap items-center justify-between gap-3" style={{ color: "var(--muted)" }}>
        <div>© {new Date().getFullYear()} Cellblock B. All Rights Reserved.</div>
        <div className="font-mono">cellblockb.com.au</div>
      </div>
    </div>
  </footer>
);

const StateFAQSchema = ({ state }) => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: `Does Cellblock B work with ${state.name} prisons?`,
        acceptedAnswer: {
          "@type": "Answer",
          text: `Cellblock B works with correctional centres in ${state.name} that allow inmates to dial out to local landline numbers. We provide you a local ${state.abbr} landline and bridge the call to your mobile.`,
        },
      },
      {
        "@type": "Question",
        name: `How fast can I get a ${state.abbr} prison call number?`,
        acceptedAnswer: {
          "@type": "Answer",
          text: "Numbers are provisioned automatically once payment is confirmed. You'll receive an email with your new local landline number within a few minutes of subscribing.",
        },
      },
      {
        "@type": "Question",
        name: "What if the inmate is transferred to another prison?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "If your loved one is transferred to another correctional facility, we provide a replacement local number where available at no extra cost.",
        },
      },
      {
        "@type": "Question",
        name: "Can I cancel anytime?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Cancel any time from your billing portal — subscriptions will not auto-renew after cancellation. We also offer a 10-day money-back guarantee.",
        },
      },
    ],
  };
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

const StateBody = ({ state }) => {
  usePageMeta(
    state.title,
    state.description,
    `https://cellblockb.com.au/${state.slug}-prison-calls`,
  );

  return (
    <div className="App relative min-h-screen" data-testid={`state-page-${state.slug}`}>
      <Hdr />
      <StateFAQSchema state={state} />

      {/* Hero */}
      <section className="py-16 sm:py-24 relative">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div className="text-xs font-mono uppercase tracking-[0.2em]" style={{ color: "var(--accent)" }}>
            {state.abbr} · Prison Call Numbers
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 leading-[1.05]">
            {state.h1}
          </h1>
          <p className="mt-6 text-lg leading-relaxed" style={{ color: "rgba(255,255,255,0.85)" }}>
            {state.intro}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/#signup"
              className="btn-primary"
              data-testid={`state-hero-cta-${state.slug}`}
              onClick={() =>
                window.cbbTrack &&
                window.cbbTrack("begin_signup", { source: `state_hero_${state.slug}` })
              }
            >
              Get My {state.abbr} Number <ArrowRight size={16} />
            </Link>
            <Link to="/#pricing" className="btn-ghost">
              See Pricing
            </Link>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-12 border-t" style={{ borderColor: "var(--line)" }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div className="text-xs font-mono uppercase tracking-[0.2em]" style={{ color: "var(--muted)" }}>
            How it works
          </div>
          <h2 className="font-display text-3xl sm:text-4xl mt-3">How {state.abbr} Prison Calls Work With Cellblock B</h2>
          <div className="grid sm:grid-cols-2 gap-6 mt-8">
            {[
              ["Choose a plan", "$1 a day — $30 every 30 days. Auto-renews until you cancel."],
              ["Get your local number", `We instantly provision a local ${state.abbr} landline number to you and email it through.`],
              ["Give the number to the inmate", "They dial it from inside the correctional centre as a normal local call."],
              ["Calls land on your mobile", "We bridge every incoming call straight to your existing mobile. No app to install. No new SIM."],
            ].map(([t, d], i) => (
              <div key={t} className="card p-5">
                <div className="text-xs font-mono" style={{ color: "var(--accent)" }}>STEP {i + 1}</div>
                <div className="font-display text-xl mt-1 text-white">{t}</div>
                <p className="mt-2 text-sm" style={{ color: "var(--muted)" }}>{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Correctional centres */}
      <section className="py-12 border-t" style={{ borderColor: "var(--line)" }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div className="text-xs font-mono uppercase tracking-[0.2em]" style={{ color: "var(--muted)" }}>
            {state.abbr} Correctional Centres
          </div>
          <h2 className="font-display text-3xl sm:text-4xl mt-3">
            We Work Across {state.name}'s Correctional Network
          </h2>
          <p className="mt-3 text-sm" style={{ color: "var(--muted)" }}>
            Cellblock B's local landline numbers can be used by inmates at any {state.abbr} correctional centre that permits outbound local calls, including:
          </p>
          <ul className="mt-6 grid sm:grid-cols-2 gap-2 text-sm">
            {state.centres.map((c) => {
              const match = PRISONS.find((p) => p.name === c);
              return (
                <li key={c} className="flex items-start gap-2.5" style={{ color: "rgba(255,255,255,0.85)" }}>
                  <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" style={{ color: "var(--accent)" }} />
                  {match ? (
                    <Link
                      to={`/${match.slug}-calls`}
                      className="hover:text-white underline-offset-2 hover:underline transition"
                      data-testid={`state-prison-link-${match.slug}`}
                    >
                      {c}
                    </Link>
                  ) : (
                    <span>{c}</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* Pricing strip */}
      <section className="py-12 border-t" style={{ borderColor: "var(--line)" }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div className="text-xs font-mono uppercase tracking-[0.2em]" style={{ color: "var(--muted)" }}>
            Pricing
          </div>
          <h2 className="font-display text-3xl sm:text-4xl mt-3">Simple, Transparent {state.abbr} Pricing</h2>
          <div className="mt-7 max-w-xl mx-auto card p-7" style={{ borderColor: "var(--accent)", borderWidth: 1, borderStyle: "solid" }}>
            <div className="text-xs font-mono uppercase tracking-[0.2em]" style={{ color: "var(--accent)" }}>Cheapest in Australia</div>
            <div className="flex items-baseline gap-2 mt-2">
              <div className="font-display text-7xl" style={{ color: "var(--accent)" }}>$1</div>
              <div className="text-sm" style={{ color: "var(--muted)" }}>/ day</div>
            </div>
            <div className="text-sm mt-1" style={{ color: "var(--muted)" }}>
              Billed <span className="text-white font-semibold">$30 every 30 days</span>. Cancel anytime from your dashboard — no contracts, no fees.
            </div>
            <Link
              to="/#signup"
              className="btn-primary mt-6 w-full justify-center"
              onClick={() => {
                window.cbbTrack && window.cbbTrack("select_plan", { plan_name: "one_dollar_day", value: 30, currency: "AUD", source: `state_${state.slug}` });
              }}
            >
              Start — $1 a day <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 border-t" style={{ borderColor: "var(--line)" }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div className="text-xs font-mono uppercase tracking-[0.2em]" style={{ color: "var(--muted)" }}>
            FAQ
          </div>
          <h2 className="font-display text-3xl sm:text-4xl mt-3">{state.abbr} Prison Calls — Common Questions</h2>
          <div className="mt-6 space-y-4">
            {[
              [
                `Does Cellblock B work with ${state.name} prisons?`,
                `Cellblock B works with correctional centres in ${state.name} that allow inmates to dial out to local landline numbers. We provide you with a local ${state.abbr} landline and bridge the call to your mobile.`,
              ],
              [
                `How fast can I get a ${state.abbr} prison call number?`,
                "Numbers are provisioned automatically once payment is confirmed. You'll receive an email with your new local landline number within a few minutes of subscribing.",
              ],
              [
                "What if the inmate is transferred to another prison?",
                "If your loved one is transferred to another correctional facility, we provide a replacement local number where available at no extra cost.",
              ],
              [
                "Can I cancel anytime?",
                "Yes. Cancel any time from your billing portal — subscriptions will not auto-renew after cancellation. We also offer a 10-day money-back guarantee.",
              ],
            ].map(([q, a]) => (
              <div key={q} className="card p-5">
                <div className="font-semibold text-white">{q}</div>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--muted)" }}>{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 border-t" style={{ borderColor: "var(--line)", background: "var(--bg-2)" }}>
        <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <h2 className="font-display text-3xl sm:text-4xl">Get your {state.name} prison call number today.</h2>
          <p className="mt-3 text-sm" style={{ color: "var(--muted)" }}>
            $1 a day. Unlimited calls. Cancel anytime.
          </p>
          <Link
            to="/#signup"
            className="btn-primary mt-7 inline-flex"
            data-testid={`state-final-cta-${state.slug}`}
            onClick={() =>
              window.cbbTrack &&
              window.cbbTrack("begin_signup", { source: `state_footer_${state.slug}` })
            }
          >
            Get My {state.abbr} Number <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* Disclaimer */}
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-8 text-xs leading-relaxed" style={{ color: "var(--muted)" }}>
        Cellblock B is independent and is not endorsed by, approved by, or affiliated with any
        Australian correctional centre, prison phone provider, or government department. Service
        suitability may depend on the rules of the relevant correctional centre.
      </div>

      <Ftr />
    </div>
  );
};

export function StatePage() {
  const { slug } = useParams();
  // Slug arrives as e.g. "qld" from route /:slug-prison-calls (we route by exact path)
  const state = STATES[slug];
  if (!state) return <Navigate to="/" replace />;
  return <StateBody state={state} />;
}

// Direct components — one per state, so routes can be /qld-prison-calls etc.
// without React Router needing path parameters. Cleaner SEO too.
export const QLDPage = () => <StateBody state={STATES.qld} />;
export const NSWPage = () => <StateBody state={STATES.nsw} />;
export const VICPage = () => <StateBody state={STATES.vic} />;
export const WAPage  = () => <StateBody state={STATES.wa} />;
export const SAPage  = () => <StateBody state={STATES.sa} />;
export const TASPage = () => <StateBody state={STATES.tas} />;
export const ACTPage = () => <StateBody state={STATES.act} />;
export const NTPage  = () => <StateBody state={STATES.nt} />;

export const STATE_SLUGS = Object.keys(STATES);
